import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { IconeColorido } from "@/components/comecar/selecao/IconeColorido";
import { iconeDaCategoria } from "@/components/comecar/selecao/icones-da-selecao";

type Opcao = { id: string; label: string };

/**
 * Especialidades do procedimento em grade de opções: as da clínica à vista, o resto atrás de
 * "Mostrar outras" (marcada fica sempre à vista).
 */
export function SeletorDeEspecialidades({
  principais,
  outras,
  marcadas,
  onChange,
  rotuloRecolher = "Mostrar só as da clínica",
  comIcones = false,
}: {
  principais: Opcao[];
  outras: Opcao[];
  marcadas: string[];
  onChange: (ids: string[]) => void;
  rotuloRecolher?: string;
  /** O ícone colorido de cada especialidade, como no criar organização. */
  comIcones?: boolean;
}) {
  const sel = new Set(marcadas);
  const [expandido, setExpandido] = useState(false);
  // Sem especialidade na clínica, não há o que esconder.
  const semPrincipais = principais.length === 0;
  const visiveis = [...principais, ...outras.filter((o) => expandido || semPrincipais || sel.has(o.id))];
  const escondidas = outras.filter((o) => !expandido && !semPrincipais && !sel.has(o.id)).length;
  const alternar = (id: string) => onChange(sel.has(id) ? marcadas.filter((x) => x !== id) : [...marcadas, id]);

  return (
    <div className="space-y-2">
      <div className="grid gap-1.5 sm:grid-cols-2">
        {visiveis.map((o) => {
          const marcada = sel.has(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => alternar(o.id)}
              aria-pressed={marcada}
              className={cn(
                "flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-[13px] transition-colors",
                marcada
                  ? "border-brand-600 bg-brand-50/70 font-medium text-zinc-900 ring-1 ring-brand-600 dark:bg-primary/10 dark:text-foreground"
                  : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground/90 dark:hover:bg-muted",
              )}
            >
              <span
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                  marcada ? "border-brand-600 bg-brand-600 text-white" : "border-zinc-300 dark:border-border",
                )}
              >
                {marcada && <Check className="h-3 w-3" />}
              </span>
              {comIcones && <IconeColorido {...iconeDaCategoria(o.label)} tamanho="sm" />}
              <span className="min-w-0">{o.label}</span>
            </button>
          );
        })}
      </div>
      {(escondidas > 0 || (expandido && !semPrincipais)) && (
        <button
          type="button"
          onClick={() => setExpandido((v) => !v)}
          aria-expanded={expandido}
          className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline dark:text-primary"
        >
          {expandido ? rotuloRecolher : `Mostrar outras especialidades (${escondidas})`}
          <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expandido && "rotate-180")} />
        </button>
      )}
    </div>
  );
}
